// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { styled } from '@mui/material/styles';
import type { AppOptions } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { Box, Container, Grid, Typography } from '../../../lib/base';
import { InstagramIcon, XIcon, YouTubeIcon } from '../../../lib/icons';
import { LinkTagUnderline, LinkUnderline } from '../../../lib/link';
import * as routes from '../../../routes';
import FacebookIcon from './FacebookIcon';
import { SocialLink } from './SocialLink';

const START_YEAR = 2022;

const StyledFacebookIcon = styled(FacebookIcon)`
  fill: ${({ theme }) => theme.custom.colors.common.white};
  padding-right: ${({ theme }) => theme.spacing(0.5)};
`;

const StyledInstagramIcon = styled(InstagramIcon)`
  fill: ${({ theme }) => theme.custom.colors.common.white};
  padding-right: ${({ theme }) => theme.spacing(0.5)};
`;

const StyledXIcon = styled(XIcon)`
  fill: ${({ theme }) => theme.custom.colors.common.white};
  padding-right: ${({ theme }) => theme.spacing(0.5)};
`;

const YTIcon = styled(YouTubeIcon)`
  fill: ${({ theme }) => theme.custom.colors.common.white};
  padding-right: ${({ theme }) => theme.spacing(0.5)};
`;

const LinkDivider = styled('div')(({ theme }) => ({
  margin: '0 1px',
  backgroundColor: theme.palette.primary.contrastText,
  width: '1px',
  height: '16px',
}));

interface Props {
  appOptions: AppOptions;
}

export function AppFooter({ appOptions }: Props): React.ReactElement {
  const year = new Date().getFullYear();
  const currentTimeIsWithinWeekOfNewYear = new Date().getMonth() === 11 && new Date().getDate() > 25;
  const currYear = currentTimeIsWithinWeekOfNewYear ? year + 1 : year;

  const BAR = 'bar';
  const LINK = 'link';
  const links: (
    | { readonly type: 'link'; readonly title: string; readonly route: string }
    | { readonly type: 'bar'; readonly id: string }
  )[] = [
    { type: LINK, title: 'Tutorials', route: routes.TUTORIALS },
    { type: BAR, id: '1' },
    { type: LINK, title: 'Blog', route: routes.BLOG },
    { type: BAR, id: '2' },
    { type: LINK, title: 'Contact Us', route: routes.CONTACT },
    { type: BAR, id: '3' },
    // { type: LINK, title: 'Team', route: routes.TEAM },
    // { type: BAR },
    { type: LINK, title: 'Terms of Service', route: routes.TERMS },
    { type: BAR, id: '4' },
    { type: LINK, title: 'Privacy Policy', route: routes.PRIVACY },
  ];

  return (
    <Box sx={{ bgcolor: 'primary.main', mt: 'auto' }}>
      <Container component="footer" disableGutters={true}>
        <Grid container sx={{ color: 'primary.contrastText', textAlign: 'center', p: { xs: 1, md: 2 } }}>
          <Grid sx={{ fontSize: '0.9rem' }} container xs={12} display="flex" justifyContent="center" columnSpacing={2}>
            <Grid xs="auto">
              <SocialLink icon={<StyledFacebookIcon />} title="Facebook" link={appOptions.meta.social.fb} />
            </Grid>
            <Grid xs="auto">
              <SocialLink icon={<StyledInstagramIcon />} title="Instagram" link={appOptions.meta.social.instagram} />
            </Grid>
            <Grid xs="auto">
              <SocialLink icon={<StyledXIcon />} title="Twitter" link={appOptions.meta.social.twitter} />
            </Grid>
            <Grid xs="auto">
              <SocialLink icon={<YTIcon />} title="YouTube" link={appOptions.meta.social.youtube} />
            </Grid>
          </Grid>
          <Grid
            container
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexShrink={0}
            xs={12}
            columnSpacing={1}
          >
            <Grid xs="auto">
              <LinkTagUnderline
                sx={{ textDecoration: 'none', color: 'primary.contrastText' }}
                href="/feedback"
                target="_blank"
              >
                <Typography>Feedback</Typography>
              </LinkTagUnderline>
            </Grid>
            <Grid xs="auto">
              <LinkDivider />
            </Grid>
            <Grid xs="auto">
              <LinkTagUnderline
                sx={{ textDecoration: 'none', color: 'primary.contrastText' }}
                href={appOptions.meta.social.fbGroup}
                target="_blank"
              >
                <Typography>Support</Typography>
              </LinkTagUnderline>
            </Grid>
            <Grid xs="auto">
              <LinkDivider />
            </Grid>
            {links.map((link, idx) => (
              <Grid key={link.type === 'bar' ? link.id : link.title} xs="auto">
                {link.type === BAR ? (
                  <LinkDivider />
                ) : (
                  <LinkUnderline sx={{ textDecoration: 'none', color: 'primary.contrastText' }} to={link.route}>
                    <Typography>{link.title}</Typography>
                  </LinkUnderline>
                )}
              </Grid>
            ))}
          </Grid>
          <Grid xs={12} sx={{ pt: 1 }}>
            <Box>
              <Typography sx={{ fontSize: '0.8rem' }}>
                {`Copyright © ${START_YEAR}${currYear === START_YEAR ? '' : `-${currYear}`} ${
                  appOptions.meta.name
                } LLC. All rights reserved.`}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
